import React from "react";
import { i18n } from "../../translate/i18n";

/**
 * Localized error boundary. Catches render/runtime errors in its children so a
 * single broken section never white-screens the whole app. Renders a small,
 * non-alarming fallback in place of the failed subtree.
 *
 * Use it to isolate optional/secondary UI (e.g. the Voice sections inside the
 * contact card, or each Voice Intelligence tab): if they throw, the host page
 * stays alive.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`,
      error,
      info?.componentStack
    );
  }

  componentDidUpdate(prevProps) {
    // Allow recovery when the keyed content changes (e.g. switching contact).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.silent) return null;
      if (this.props.page) {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: 12,
              color: "#555",
              textAlign: "center",
              padding: 24,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {i18n.t("errorBoundary.pageTitle") || "משהו השתבש בטעינת המסך"}
            </div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {i18n.t("errorBoundary.pageBody") ||
                "נסה לרענן. שאר המערכת ממשיכה לעבוד כרגיל."}
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginTop: 4,
                padding: "8px 18px",
                borderRadius: 8,
                border: "none",
                background: "#0f6e56",
                color: "#fff",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              {i18n.t("errorBoundary.reload") || "רענן"}
            </button>
          </div>
        );
      }
      return (
        <div
          style={{
            fontSize: 12,
            color: "#9a6b00",
            background: "rgba(255, 193, 7, 0.08)",
            border: "1px solid rgba(255, 193, 7, 0.3)",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          {i18n.t("errorBoundary.sectionFailed") ||
            "לא ניתן לטעון אזור זה כרגע."}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
